var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Paper } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { theme } from '../../config';
import { PlanType } from '../../enum';
import { formatDateToLocaleUppercase } from '../../util';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '../common';
import { useReportAPI } from '../report/selectors';
import { DailyMetricHierarchyRows, FixedTableBanner, LEVEL_ROW_INDENTS, LEVEL_ROW_STICKIES, StyledDailyHeaderCell, StyledDateLink, StyledLevelFirstCell, buildIntervalUrl, } from './common';
import { PROD_HOURS_DECIMAL_VALUE, PROD_HOURS_LABELS, PROD_HOURS_LABELS_WITHOUT_PRODUCTIVE, PROD_HOURS_LABEL_INDEX, } from './constants';
import { useDailyContext } from './context';
import { ContentNav, StyledAutocomplete, StyledJumpTo, TableNavBar } from './detailTable';
import { selectTimes, useDailyAPI, useDailyReportPlan, useDailySummaryData, useDaysOfWeek, usePlanColor, useProdHoursTimelineOptions, } from './selectors';
var getRowColor = function (rowKind, isTotalRow, bodyColor) {
    if (rowKind === 'location')
        return theme.palette.background.default;
    if (isTotalRow)
        return bodyColor;
    return undefined;
};
var isOccupancy100Percent = function (occupancy) {
    if (occupancy === null || occupancy === undefined)
        return false;
    var numValue = typeof occupancy === 'string' ? Number.parseFloat(occupancy) : occupancy;
    if (Number.isNaN(numValue))
        return false;
    return numValue === 1 || numValue === 100;
};
var buildProdHoursMetrics = function (hideProductiveHours) {
    var labels = hideProductiveHours ? PROD_HOURS_LABELS_WITHOUT_PRODUCTIVE : PROD_HOURS_LABELS;
    return labels.map(function (label) {
        var _a;
        return ({
            key: label,
            name: label,
            idx: PROD_HOURS_LABEL_INDEX[label],
            decimal: (_a = PROD_HOURS_DECIMAL_VALUE[label]) !== null && _a !== void 0 ? _a : 0,
        });
    });
};
var getProdHoursDetail = function (dailyData, periodIndex) {
    var monthData = dailyData === null || dailyData === void 0 ? void 0 : dailyData[periodIndex];
    return (monthData === null || monthData === void 0 ? void 0 : monthData.detail) || {};
};
var getProdHoursMonthData = function (dailyData, periodIndex) {
    return dailyData === null || dailyData === void 0 ? void 0 : dailyData[periodIndex];
};
var toDetailData = function (detail) { return detail; };
var formatIntervalHeaderTime = function (time) {
    var parsedTime = moment.utc(time, ['HH:mm', 'hh:mmA'], true);
    return parsedTime.isValid() ? parsedTime.format('hh:mmA') : time;
};
export var ProdHoursDetailTable = function () {
    var _a, _b, _c, _d, _e, _f;
    var _g = useTranslation(['report', 'translation']), i18n = _g.i18n, t = _g.t;
    var navigate = useNavigate();
    var basePlanId = useParams().basePlanId;
    var _h = useDailyContext(), state = _h[0], setState = _h[1];
    var _j = useSearchParams(), searchParams = _j[0], setSearchParams = _j[1];
    var dailyData = useDailyAPI()[0];
    var reportData = useReportAPI()[0].data;
    var _k = usePlanColor(), headerColor = _k[0], bodyColor = _k[1];
    var timelines = useProdHoursTimelineOptions();
    var dailyReportData = useDailyReportPlan();
    var daysOfWeek = useDaysOfWeek();
    var times = React.useMemo(function () { var _a; return (_a = selectTimes(dailyData.data)) !== null && _a !== void 0 ? _a : []; }, [dailyData.data]);
    var summaryData = useDailySummaryData();
    var periodIndex = (_a = state === null || state === void 0 ? void 0 : state.month) !== null && _a !== void 0 ? _a : 0;
    var detail = React.useMemo(function () { return getProdHoursDetail(dailyData.data, periodIndex); }, [dailyData.data, periodIndex]);
    var monthData = React.useMemo(function () { return getProdHoursMonthData(dailyData.data, periodIndex); }, [dailyData.data, periodIndex]);
    var tableDetail = React.useMemo(function () { return toDetailData(detail); }, [detail]);
    var isInterval = (state === null || state === void 0 ? void 0 : state.viewType) === 'interval';
    var activeDate = (_b = state === null || state === void 0 ? void 0 : state.activeDate) !== null && _b !== void 0 ? _b : (_c = daysOfWeek[0]) === null || _c === void 0 ? void 0 : _c.id;
    var activeDayIndex = React.useMemo(function () {
        if (!activeDate)
            return 0;
        var dayIndex = daysOfWeek.findIndex(function (day) { return day.id === activeDate; });
        return dayIndex >= 0 ? dayIndex : moment.utc(activeDate).isoWeekday() - 1;
    }, [activeDate, daysOfWeek]);
    var visibleDayIndices = React.useMemo(function () { return daysOfWeek.map(function (__day, index) { return index; }); }, [daysOfWeek]);
    var hideProductiveHours = React.useMemo(function () {
        var _a;
        if (((_a = summaryData === null || summaryData === void 0 ? void 0 : summaryData.normal) === null || _a === void 0 ? void 0 : _a.forecastedOccupancy) && Array.isArray(summaryData.normal.forecastedOccupancy)) {
            var occupancyValue = summaryData.normal.forecastedOccupancy[periodIndex];
            if (occupancyValue !== undefined && occupancyValue !== null) {
                if (isOccupancy100Percent(occupancyValue))
                    return true;
            }
        }
        if (!detail)
            return false;
        var tolerance = 0.1;
        var hasData = false;
        var allProductiveZero = true;
        var checkedCount = 0;
        var totalProductiveValues = [];
        for (var _i = 0, visibleDayIndices_1 = visibleDayIndices; _i < visibleDayIndices_1.length; _i++) {
            var dayIndex = visibleDayIndices_1[_i];
            var dayValues = detail[dayIndex];
            if (!dayValues)
                continue;
            var dayTotal = dayValues['total'];
            if (Array.isArray(dayTotal) && dayTotal.length > 1) {
                var totalProductive = Number(dayTotal[0]);
                if (!Number.isNaN(totalProductive)) {
                    totalProductiveValues.push(totalProductive);
                }
            }
            for (var _b = 0, times_1 = times; _b < times_1.length; _b++) {
                var timeKey = times_1[_b];
                var values = dayValues[timeKey];
                if (!Array.isArray(values) || values.length < 2)
                    continue;
                var productiveHours = Number(values[0]);
                var productionHours = Number(values[1]);
                if (!Number.isNaN(productiveHours) && !Number.isNaN(productionHours)) {
                    hasData = true;
                    if (productiveHours > 0) {
                        allProductiveZero = false;
                    }
                    if (productiveHours > 0 && productionHours > 0) {
                        checkedCount++;
                        if (Math.abs(productiveHours - productionHours) > tolerance)
                            return false;
                    }
                }
            }
        }
        if (hasData && allProductiveZero)
            return true;
        if (totalProductiveValues.length > 0 && totalProductiveValues.every(function (value) { return value === 0; }))
            return true;
        return checkedCount >= 3;
    }, [detail, periodIndex, summaryData, times, visibleDayIndices]);
    var visibleMetrics = React.useMemo(function () { return buildProdHoursMetrics(hideProductiveHours); }, [hideProductiveHours]);
    var handlePeriodChange = function (month) {
        var isMonthly = (reportData === null || reportData === void 0 ? void 0 : reportData.planType) === PlanType.Monthly || (reportData === null || reportData === void 0 ? void 0 : reportData.planType) === PlanType.MonthlyCustom;
        var periodUnit = isMonthly ? 'months' : 'weeks';
        var nextPeriodStart = (reportData === null || reportData === void 0 ? void 0 : reportData.startDate)
            ? moment.utc(reportData.startDate).add(month, periodUnit).format('YYYY-MM-DD')
            : undefined;
        setState(function (curr) {
            return curr
                ? __assign(__assign(__assign({}, curr), { month: month, startMonth: month, endMonth: month }), (isInterval && nextPeriodStart ? { activeDate: nextPeriodStart } : {})) : curr;
        });
    };
    var handlePrevPeriod = function () {
        if (!timelines.length)
            return;
        var idx = timelines.findIndex(function (timeline) { return timeline.id === periodIndex; });
        if (idx > 0)
            handlePeriodChange(timelines[idx - 1].id);
    };
    var handleNextPeriod = function () {
        if (!timelines.length)
            return;
        var idx = timelines.findIndex(function (timeline) { return timeline.id === periodIndex; });
        if (idx >= 0 && idx < timelines.length - 1)
            handlePeriodChange(timelines[idx + 1].id);
    };
    var handleDateClick = function (dateStr) {
        if (!basePlanId)
            return;
        navigate(buildIntervalUrl(basePlanId, state, dateStr));
    };
    var handlePrevDay = function () {
        if (!activeDate || !daysOfWeek.length)
            return;
        var idx = daysOfWeek.findIndex(function (day) { return day.id === activeDate; });
        if (idx > 0) {
            setState(function (curr) { return (curr ? __assign(__assign({}, curr), { activeDate: daysOfWeek[idx - 1].id }) : curr); });
        }
    };
    var handleNextDay = function () {
        if (!activeDate || !daysOfWeek.length)
            return;
        var idx = daysOfWeek.findIndex(function (day) { return day.id === activeDate; });
        if (idx >= 0 && idx < daysOfWeek.length - 1) {
            setState(function (curr) { return (curr ? __assign(__assign({}, curr), { activeDate: daysOfWeek[idx + 1].id }) : curr); });
        }
    };
    var renderDailyValueCells = function (source, metric, rowKind) {
        var _a, _b, _c;
        return (_jsxs(_Fragment, { children: [visibleDayIndices.map(function (dayIndex) {
                    var _a, _b, _c;
                    return (_jsx(TableCell, { value: (_c = (_b = (_a = source.detail) === null || _a === void 0 ? void 0 : _a[dayIndex]) === null || _b === void 0 ? void 0 : _b['total']) === null || _c === void 0 ? void 0 : _c[metric.idx], decimal: metric.decimal, color: getRowColor(rowKind, source.detail === tableDetail, bodyColor), align: "center" }, dayIndex));
                }), _jsx(TableCell, { value: (_c = (_b = (_a = source.detail) === null || _a === void 0 ? void 0 : _a['total']) === null || _b === void 0 ? void 0 : _b['total']) === null || _c === void 0 ? void 0 : _c[metric.idx], decimal: metric.decimal, color: bodyColor, align: "center" }), _jsx(TableCell, { value: "", color: bodyColor, align: "center" })] }));
    };
    var renderDailyBlankCells = function (rowColor) { return (_jsxs(_Fragment, { children: [visibleDayIndices.map(function (dayIndex) { return (_jsx(TableCell, { color: rowColor }, dayIndex)); }), _jsx(TableCell, { color: rowColor }), _jsx(TableCell, { color: rowColor })] })); };
    var renderIntervalValueCells = function (source, metric, rowKind) { return (_jsx(_Fragment, { children: times.map(function (time) {
            var _a, _b, _c;
            return (_jsx(TableCell, { value: (_c = (_b = (_a = source.detail) === null || _a === void 0 ? void 0 : _a[activeDayIndex]) === null || _b === void 0 ? void 0 : _b[time]) === null || _c === void 0 ? void 0 : _c[metric.idx], decimal: metric.decimal, color: getRowColor(rowKind, source.detail === tableDetail, bodyColor), align: "center" }, time));
        }) })); };
    var renderIntervalBlankCells = function (rowColor) { return (_jsx(_Fragment, { children: times.map(function (time) { return (_jsx(TableCell, { color: rowColor }, time)); }) })); };
    var currentTimelineLabel = ((_d = timelines.find(function (timeline) { return timeline.id === periodIndex; })) === null || _d === void 0 ? void 0 : _d.name) || '';
    var currentDayLabel = ((_e = daysOfWeek.find(function (day) { return day.id === activeDate; })) === null || _e === void 0 ? void 0 : _e.name) || '';
    var dayName = activeDate
        ? formatDateToLocaleUppercase(moment.utc(activeDate).locale(i18n.language).format('dddd'))
        : '';
    return (_jsxs(Paper, __assign({ elevation: 0, sx: { backgroundColor: theme.palette.background.default } }, { children: [_jsxs(ContentNav, __assign({ sx: { mb: 2 } }, { children: [_jsx(TableNavBar, { onNext: isInterval ? handleNextDay : handleNextPeriod, onPrev: isInterval ? handlePrevDay : handlePrevPeriod, title: isInterval ? currentDayLabel : currentTimelineLabel, disabledNext: isInterval
                            ? daysOfWeek.findIndex(function (day) { return day.id === activeDate; }) >= daysOfWeek.length - 1
                            : periodIndex === timelines.length - 1, disabledPrev: isInterval ? daysOfWeek.findIndex(function (day) { return day.id === activeDate; }) <= 0 : !periodIndex }), _jsxs(StyledJumpTo, __assign({ sx: { gap: 2 } }, { children: [_jsx(StyledAutocomplete, { options: dailyReportData || [], getOptionValue: function (option) { return option.uniqueId; }, value: (state === null || state === void 0 ? void 0 : state.uniqueId) || '', onChange: function (value) {
                                    if (value) {
                                        var params = new URLSearchParams(searchParams);
                                        params.set('uniqueId', value);
                                        setSearchParams(params, { replace: true });
                                    }
                                }, disabled: !(dailyReportData === null || dailyReportData === void 0 ? void 0 : dailyReportData.length) }), _jsx(StyledAutocomplete, { options: timelines, value: periodIndex, onChange: function (value) {
                                    if (value !== undefined && value !== null)
                                        handlePeriodChange(Number(value));
                                }, disabled: !timelines.length })] }))] })), _jsx(FixedTableBanner, { children: isInterval ? dayName : (_f = daysOfWeek[0]) === null || _f === void 0 ? void 0 : _f.label }), _jsx(TableContainer, __assign({ maxHeight: "38rem" }, { children: _jsxs(Table, __assign({ sx: { tableLayout: 'auto' } }, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(StyledLevelFirstCell, { component: "td", greyColor: true, stickySizes: LEVEL_ROW_STICKIES, "aria-label": LEVEL_ROW_INDENTS.metric, sx: { width: LEVEL_ROW_STICKIES[0], backgroundColor: '#e4e4e4 !important' } }), isInterval
                                        ? times.map(function (time) { return (_jsx(StyledDailyHeaderCell, __assign({ greyColor: true, align: "center", sx: { color: headerColor } }, { children: formatIntervalHeaderTime(time) }), time)); })
                                        : daysOfWeek.map(function (day) { return (_jsx(StyledDailyHeaderCell, __assign({ align: "center" }, { children: _jsx(StyledDateLink, __assign({ onClick: function () { return handleDateClick(day.id); }, sx: { color: headerColor } }, { children: day.label })) }), day.id)); }), !isInterval && (_jsxs(_Fragment, { children: [_jsx(StyledDailyHeaderCell, __assign({ align: "center" }, { children: t('daily.totalAverage', { defaultValue: 'Total/Average' }) })), _jsx(StyledDailyHeaderCell, __assign({ align: "center" }, { children: t('daily.staffHours', { defaultValue: 'Staff Hours' }) }))] }))] }) }), _jsx(TableBody, { children: _jsx(DailyMetricHierarchyRows, { visibleMetrics: visibleMetrics, detail: tableDetail, countries: (monthData === null || monthData === void 0 ? void 0 : monthData.countries) || [], queuesByLob: monthData === null || monthData === void 0 ? void 0 : monthData.queuesByLob, bodyColor: bodyColor, renderValueCells: isInterval ? renderIntervalValueCells : renderDailyValueCells, renderBlankCells: isInterval ? renderIntervalBlankCells : renderDailyBlankCells }) })] })) }))] })));
};
